import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Package, Trash2, Star, StarOff, Leaf, Flower2, Sparkles } from 'lucide-react';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import { DataTable, useDataTableState } from '@/components/admin/DataTable';
import type { ColumnDef, BulkAction, PaginationData, DirtyCell } from '@/components/admin/DataTable';
import api from '@/lib/api';
import { formatCurrency, cn } from '@/lib/utils';
import { useUIStore } from '@/stores/ui-store';

interface Product {
  id: string;
  name: string;
  slug: string;
  status: 'draft' | 'active' | 'archived';
  base_price: number;
  sku: string;
  primary_image_url: string | null;
  variant_count: number;
  total_stock: number;
  category_name: string | null;
  is_featured: boolean;
  is_organic: boolean;
  is_heirloom: boolean;
  compare_at_price: number | null;
  cost_price: number | null;
  fulfillment_type: 'self' | 'dropship' | 'hybrid';
  supplier_name: string | null;
  created_at: string;
  updated_at: string;
  ai_text_model?: string;
}

interface ProductsResponse {
  products: Product[];
  pagination: PaginationData;
}

type StatusFilter = 'all' | 'active' | 'draft' | 'archived';

const getStatusBadge = (status: Product['status']) => {
  const variants = { active: 'green' as const, draft: 'yellow' as const, archived: 'gray' as const };
  return variants[status];
};

const getStockStatus = (stock: number) => {
  if (stock === 0) return { variant: 'red' as const, label: 'Out of stock' };
  if (stock < 10) return { variant: 'yellow' as const, label: 'Low stock' };
  return { variant: 'green' as const, label: 'In stock' };
};

const columns: ColumnDef<Product>[] = [
  {
    id: 'image',
    header: 'Image',
    hideable: false,
    width: 'w-16',
    cell: ({ row }) => (
      <Link to={`/admin/products/${row.id}`} className="block">
        {row.primary_image_url ? (
          <img src={row.primary_image_url} alt="" className="w-12 h-12 object-cover rounded-lg" />
        ) : (
          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
            <Package className="w-6 h-6 text-gray-400" />
          </div>
        )}
      </Link>
    ),
  },
  {
    id: 'name',
    header: 'Product',
    accessorKey: 'name',
    sortable: true,
    editable: true,
    editType: 'text',
    cell: ({ row }) => (
      <Link to={`/admin/products/${row.id}`} className="block group">
        <div className="flex items-center gap-1.5 font-medium text-gray-900 group-hover:text-blue-600">
          {row.name}
          {row.ai_text_model && (
            <span title={`AI: ${row.ai_text_model}`} className="inline-flex items-center">
              <Sparkles className="w-3.5 h-3.5 text-purple-400" />
            </span>
          )}
        </div>
        <div className="text-sm text-gray-500">SKU: {row.sku}</div>
      </Link>
    ),
  },
  {
    id: 'sku',
    header: 'SKU',
    accessorKey: 'sku',
    editable: true,
    editType: 'text',
    hideable: true,
    defaultVisible: false,
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    editable: true,
    editType: 'select',
    editOptions: [
      { label: 'Active', value: 'active' },
      { label: 'Draft', value: 'draft' },
      { label: 'Archived', value: 'archived' },
    ],
    cell: ({ row }) => (
      <Badge variant={getStatusBadge(row.status)}>{row.status}</Badge>
    ),
  },
  {
    id: 'category',
    header: 'Category',
    accessorKey: 'category_name',
    cell: ({ row }) =>
      row.category_name || <span className="text-gray-400">Uncategorized</span>,
  },
  {
    id: 'base_price',
    header: 'Price',
    accessorKey: 'base_price',
    sortable: true,
    editable: true,
    editType: 'number',
    align: 'right',
    cell: ({ row }) => (
      <span className="font-medium text-gray-900">{formatCurrency(row.base_price)}</span>
    ),
  },
  {
    id: 'is_featured',
    header: 'Featured',
    accessorKey: 'is_featured',
    editable: true,
    editType: 'select',
    editOptions: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
    hideable: true,
    defaultVisible: false,
    cell: ({ row }) => (
      row.is_featured
        ? <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
        : <StarOff className="w-4 h-4 text-gray-300" />
    ),
  },
  {
    id: 'fulfillment_type',
    header: 'Fulfillment',
    accessorKey: 'fulfillment_type',
    editable: true,
    editType: 'select',
    editOptions: [
      { label: 'Self-fulfilled', value: 'self' },
      { label: 'Dropship', value: 'dropship' },
      { label: 'Hybrid', value: 'hybrid' },
    ],
    hideable: true,
    defaultVisible: false,
    cell: ({ row }) => {
      const labels: Record<string, string> = { self: 'Self', dropship: 'Dropship', hybrid: 'Hybrid' };
      const variants: Record<string, 'green' | 'blue' | 'yellow'> = { self: 'green', dropship: 'blue', hybrid: 'yellow' };
      return <Badge variant={variants[row.fulfillment_type] || 'gray'}>{labels[row.fulfillment_type] || row.fulfillment_type}</Badge>;
    },
  },
  {
    id: 'total_stock',
    header: 'Stock',
    accessorKey: 'total_stock',
    sortable: true,
    align: 'right',
    cell: ({ row }) => {
      const stockStatus = getStockStatus(row.total_stock);
      return (
        <div className="flex items-center justify-end gap-2">
          <span className="font-medium text-gray-900">{row.total_stock}</span>
          <Badge variant={stockStatus.variant} className="min-w-[5rem] justify-center">{stockStatus.label}</Badge>
        </div>
      );
    },
  },
  {
    id: 'variant_count',
    header: 'Variants',
    accessorKey: 'variant_count',
    cell: ({ row }) => (
      <span className="text-sm text-gray-600">
        {row.variant_count} {row.variant_count === 1 ? 'variant' : 'variants'}
      </span>
    ),
  },
  {
    id: 'cost_price',
    header: 'Cost',
    accessorKey: 'cost_price',
    sortable: true,
    editable: true,
    editType: 'number',
    align: 'right',
    hideable: true,
    defaultVisible: false,
    cell: ({ row }) => (
      row.cost_price != null
        ? <span className="text-sm text-gray-700">{formatCurrency(row.cost_price)}</span>
        : <span className="text-sm text-gray-400">—</span>
    ),
  },
  {
    id: 'compare_at_price',
    header: 'Compare At',
    accessorKey: 'compare_at_price',
    sortable: true,
    editable: true,
    editType: 'number',
    align: 'right',
    hideable: true,
    defaultVisible: false,
    cell: ({ row }) => (
      row.compare_at_price != null
        ? <span className="text-sm text-gray-700 line-through">{formatCurrency(row.compare_at_price)}</span>
        : <span className="text-sm text-gray-400">—</span>
    ),
  },
  {
    id: 'is_organic',
    header: 'Organic',
    accessorKey: 'is_organic',
    hideable: true,
    defaultVisible: false,
    cell: ({ row }) => (
      row.is_organic
        ? <Leaf className="w-4 h-4 text-green-600" />
        : <span className="text-sm text-gray-400">—</span>
    ),
  },
  {
    id: 'is_heirloom',
    header: 'Heirloom',
    accessorKey: 'is_heirloom',
    hideable: true,
    defaultVisible: false,
    cell: ({ row }) => (
      row.is_heirloom
        ? <Flower2 className="w-4 h-4 text-purple-600" />
        : <span className="text-sm text-gray-400">—</span>
    ),
  },
  {
    id: 'supplier_name',
    header: 'Supplier',
    accessorKey: 'supplier_name',
    hideable: true,
    defaultVisible: false,
    cell: ({ row }) => (
      row.supplier_name
        ? <span className="text-sm text-gray-700">{row.supplier_name}</span>
        : <span className="text-sm text-gray-400">—</span>
    ),
  },
  {
    id: 'created_at',
    header: 'Created',
    accessorKey: 'created_at',
    sortable: true,
    hideable: true,
    defaultVisible: false,
    cell: ({ row }) => (
      <span className="text-sm text-gray-500">
        {new Date(row.created_at).toLocaleDateString()}
      </span>
    ),
  },
  {
    id: 'updated_at',
    header: 'Updated',
    accessorKey: 'updated_at',
    sortable: true,
    hideable: true,
    defaultVisible: false,
    cell: ({ row }) => (
      <span className="text-sm text-gray-500">
        {new Date(row.updated_at).toLocaleDateString()}
      </span>
    ),
  },
];

const Products = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1, limit: 25, total: 0, total_pages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const addToast = useUIStore((s) => s.addToast);

  const { state, updateState, markDirty } = useDataTableState({
    storageKey: 'admin-products',
    defaultSort: 'name',
    defaultLimit: 25,
    columns,
    filterKeys: ['status'],
  });

  const statusFilter = (state.filters.status || 'all') as StatusFilter;

  // Fetch products when state changes
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const params = new URLSearchParams();
        params.set('page', state.page.toString());
        params.set('limit', state.limit.toString());
        if (state.search) params.set('q', state.search);
        if (statusFilter !== 'all') params.set('status', statusFilter);
        if (state.sort) params.set('sort', state.sort);
        params.set('order', state.order);

        const response = await api.get<ProductsResponse>(`/admin/products?${params.toString()}`);
        setProducts(response.products);
        setPagination(response.pagination);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load products');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [state.page, state.limit, state.search, statusFilter, state.sort, state.order]);

  const handleStatusFilter = (status: StatusFilter) => {
    updateState({ filters: { ...state.filters, status: status === 'all' ? '' : status } });
  };

  const handleBatchSave = useCallback(async (changes: DirtyCell[]) => {
    // Group changes by row
    const byRow = new Map<string, Record<string, unknown>>();
    for (const cell of changes) {
      if (!byRow.has(cell.rowId)) byRow.set(cell.rowId, {});
      const fields = byRow.get(cell.rowId)!;
      // Convert boolean string values for is_featured
      if (cell.columnId === 'is_featured') {
        fields[cell.columnId] = cell.value === 'true' || cell.value === true;
      } else {
        fields[cell.columnId] = cell.value;
      }
    }

    // Save each row
    const errors: string[] = [];
    for (const [rowId, fields] of byRow) {
      try {
        await api.patch(`/admin/products/${rowId}`, fields);
        // Update local state
        setProducts((prev) =>
          prev.map((p) => (p.id === rowId ? { ...p, ...fields } : p))
        );
      } catch (err: any) {
        errors.push(err.message || `Failed to update product ${rowId}`);
      }
    }

    if (errors.length > 0) {
      addToast(`${errors.length} update(s) failed`, 'error');
      throw new Error('Some updates failed');
    }
    addToast(`${byRow.size} product(s) updated`, 'success');
  }, [addToast]);

  const handleDirtyChange = useCallback(
    (rowId: string, columnId: string, value: unknown, originalValue: unknown) => {
      markDirty(rowId, columnId, value, originalValue);
    },
    [markDirty]
  );

  const bulkActions: BulkAction<Product>[] = [
    {
      id: 'archive',
      label: 'Archive',
      icon: <Trash2 className="w-4 h-4 mr-1" />,
      variant: 'danger',
      confirmMessage: 'Are you sure you want to archive the selected products?',
      onExecute: async (rows) => {
        const ids = rows.map((r) => r.id);
        await api.post('/admin/products/bulk', { action: 'delete', ids });
        setProducts((prev) => prev.filter((p) => !ids.includes(p.id)));
        addToast(`${ids.length} product(s) archived`, 'success');
      },
    },
    {
      id: 'activate',
      label: 'Set Active',
      variant: 'primary',
      onExecute: async (rows) => {
        const ids = rows.map((r) => r.id);
        await api.post('/admin/products/bulk', { action: 'update_status', ids, data: { status: 'active' } });
        setProducts((prev) =>
          prev.map((p) => (ids.includes(p.id) ? { ...p, status: 'active' as const } : p))
        );
        addToast(`${ids.length} product(s) set to active`, 'success');
      },
    },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-600 mt-1">Manage your catalog and inventory</p>
        </div>
        <Link to="/admin/products/new">
          <Button variant="primary" size="md">
            <Plus className="w-5 h-5 mr-2" />
            Add Product
          </Button>
        </Link>
      </div>

      {/* Status Filter Tabs */}
      <Card padding="md">
        <div className="flex gap-2 border-b border-gray-200">
          {(['all', 'active', 'draft', 'archived'] as StatusFilter[]).map((status) => (
            <button
              key={status}
              onClick={() => handleStatusFilter(status)}
              className={cn(
                'px-4 py-2 font-medium text-sm border-b-2 transition-colors',
                statusFilter === status
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              )}
              aria-current={statusFilter === status ? 'page' : undefined}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </Card>

      {/* Products DataTable */}
      <DataTable
        columns={columns}
        data={products}
        pagination={pagination}
        isLoading={isLoading}
        error={error}
        state={state}
        onStateChange={updateState}
        rowKey="id"
        onBatchSave={handleBatchSave}
        onDirtyChange={handleDirtyChange}
        bulkActions={bulkActions}
        searchPlaceholder="Search products by name, SKU, or category..."
        emptyState={
          <div className="p-12 text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {state.search || statusFilter !== 'all' ? 'No products found' : 'No products yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {state.search || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by adding your first product'}
            </p>
            {!state.search && statusFilter === 'all' && (
              <Link to="/admin/products/new">
                <Button variant="primary" size="md">
                  <Plus className="w-5 h-5 mr-2" />
                  Add Product
                </Button>
              </Link>
            )}
          </div>
        }
      />
    </div>
  );
};

export default Products;
